import { kCitationIdRegex, kCombinedCitationRegex, readCitationMatch, replaceCitations } from '@/util/agent-citations'
import { agentV2SourceKey } from '@/agent-v2/references/agent-v2-source-key'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'

// An unresolved citation renders visibly grey, never hidden.
export const kAgentV2UnverifiedCitationLabel = '?'
export const kAgentV2UnverifiedSourceLabel = 'Not linked — verify independently'
export const kAgentV2SourceUnavailableLabel = 'Source unavailable'

// Citation hrefs carry the form as well as the id so the markdown anchor override can
// style badges and legal-citation links differently.
export const kAgentV2CitationHrefPrefix = '#agent-v2-citation-'
export const kAgentV2LegalCitationHrefPrefix = '#agent-v2-legal-citation-'

/**
 * One citation occurrence in a message, in document order.
 *
 * Produced by labelAgentV2Citations and consumed positionally: the i-th entry describes
 * the i-th citation token the grammar finds in the text, so every consumer that walks the
 * same text with the same grammar (replaceAgentV2CitationTokens below) stays aligned by a
 * single shared index — no per-id bookkeeping.
 */
export type AgentV2LabeledCitation = {
  citationId: string
  /** The authority text of a legal citation link; null for a reference marker. */
  authorityText: string | null
  /** False when the citation id has no reference in the conversation map. */
  isResolved: boolean
  /** The cited source's footnote number; null when unresolved. */
  footnoteNumber: number | null
  /** Footnote label for this occurrence ("2", "2.1", ...); null when unresolved. */
  label: string | null
}

/**
 * Assign display-time footnote labels to every citation occurrence in one message.
 *
 * Tokenization comes from the shared display grammar (`@/util/agent-citations`) —
 * reference markers "[ref_ab12cdXYZ]" and legal citation links "[authority](ref_ab12cdXYZ)"
 * are both occurrences of their source.
 *
 * Numbering follows the document-export contract (`paxton_app/agent_v2/citations`):
 * grouping by source key (`agentV2SourceKey`), marker-cited sources number first —
 * 1..M in citation order, so every number visible in the text is contiguous — and
 * sources cited only through legal links follow (M+1..). A marker's first occurrence
 * labels "N" and later marker occurrences "N.1", "N.2", .... Legal links display
 * their authority text instead and never consume a marker occurrence slot. Their
 * label — the reference-section chip and the hover card, not the inline text — is
 * the next suffix after every marker label that source shows, and a repeated id
 * reuses the label of its first occurrence. Unresolved ids never consume a number,
 * so a missing reference cannot shift the numbering of everything after it.
 */
export function labelAgentV2Citations(text: string, referencesByCitationId: Record<string, AgentV2Reference> | undefined): AgentV2LabeledCitation[] {
  const occurrences = Array.from(text.matchAll(kCombinedCitationRegex), (match) => {
    const { citationId, authorityText } = readCitationMatch(match)
    const reference = kCitationIdRegex.test(citationId) ? referencesByCitationId?.[citationId] : undefined
    return { citationId, authorityText, sourceKey: reference ? agentV2SourceKey(reference) : null }
  })

  const footnoteNumberBySource = new Map<string, number>()
  const assignNumber = (sourceKey: string) => {
    if (!footnoteNumberBySource.has(sourceKey)) footnoteNumberBySource.set(sourceKey, footnoteNumberBySource.size + 1)
  }
  for (const occurrence of occurrences) {
    if (occurrence.sourceKey !== null && occurrence.authorityText === null) assignNumber(occurrence.sourceKey)
  }
  for (const occurrence of occurrences) {
    if (occurrence.sourceKey !== null) assignNumber(occurrence.sourceKey)
  }

  // Marker labels are assigned first, including repeats of one id, so a later legal
  // link cannot reuse a number the message already shows on a badge.
  const markerCountBySource = new Map<string, number>()
  const firstLabelByCitationId = new Map<string, string>()
  const markerLabels = occurrences.map(({ citationId, authorityText, sourceKey }) => {
    if (sourceKey === null || authorityText !== null) return null
    const footnoteNumber = footnoteNumberBySource.get(sourceKey)
    if (footnoteNumber === undefined) return null
    const occurrenceIndex = markerCountBySource.get(sourceKey) ?? 0
    markerCountBySource.set(sourceKey, occurrenceIndex + 1)
    const label = footnoteLabel(footnoteNumber, occurrenceIndex)
    if (!firstLabelByCitationId.has(citationId)) firstLabelByCitationId.set(citationId, label)
    return label
  })

  const linkCountBySource = new Map<string, number>()
  return occurrences.map(({ citationId, authorityText, sourceKey }, index): AgentV2LabeledCitation => {
    const footnoteNumber = sourceKey === null ? undefined : footnoteNumberBySource.get(sourceKey)
    if (footnoteNumber === undefined || sourceKey === null) return { citationId, authorityText, isResolved: false, footnoteNumber: null, label: null }

    if (authorityText === null) {
      return { citationId, authorityText, isResolved: true, footnoteNumber, label: markerLabels[index] }
    }

    const existingLabel = firstLabelByCitationId.get(citationId)
    if (existingLabel !== undefined) return { citationId, authorityText, isResolved: true, footnoteNumber, label: existingLabel }

    const occurrenceIndex = (markerCountBySource.get(sourceKey) ?? 0) + (linkCountBySource.get(sourceKey) ?? 0)
    linkCountBySource.set(sourceKey, (linkCountBySource.get(sourceKey) ?? 0) + 1)
    const label = footnoteLabel(footnoteNumber, occurrenceIndex)
    firstLabelByCitationId.set(citationId, label)
    return { citationId, authorityText, isResolved: true, footnoteNumber, label }
  })
}

function footnoteLabel(footnoteNumber: number, occurrenceIndex: number): string {
  return occurrenceIndex === 0 ? `${footnoteNumber}` : `${footnoteNumber}.${occurrenceIndex}`
}

/** The footnote number a text shows for each source it cites, keyed by agentV2SourceKey. */
export function getMarkerFootnoteNumberBySourceKey(
  text: string,
  referencesByCitationId: Record<string, AgentV2Reference> | undefined
): Partial<{ [sourceKey: string]: number }> {
  const footnoteNumberBySourceKey: Partial<{ [sourceKey: string]: number }> = {}
  for (const citation of labelAgentV2Citations(text, referencesByCitationId)) {
    const reference = referencesByCitationId?.[citation.citationId]
    // Only a marker shows its number; a legal citation link shows the authority text instead.
    if (citation.authorityText !== null || citation.footnoteNumber === null || !reference) continue
    footnoteNumberBySourceKey[agentV2SourceKey(reference)] = citation.footnoteNumber
  }
  return footnoteNumberBySourceKey
}

/**
 * Rewrite citation tokens for markdown rendering. Every token becomes a link on the
 * citation href so the markdown renderer's anchor override owns presentation: markers
 * show their footnote label (or the grey "?" when unresolved), legal links keep their
 * authority text. Nothing is ever removed — unresolved citations render greyed-out and
 * non-clickable.
 */
export function replaceAgentV2CitationTokens(text: string, labeledCitations: AgentV2LabeledCitation[]): string {
  let occurrenceIndex = 0

  return replaceCitations(text, (token, citation) => {
    const labeled = labeledCitations[occurrenceIndex]
    occurrenceIndex += 1
    // Labeling and replacement walk the same text with the same grammar, so a mismatch
    // means the inputs diverged (e.g. labels built from different text) — keep the raw
    // token rather than mislabeling it.
    if (!labeled || labeled.citationId !== citation.citationId) return token

    if (citation.authorityText !== null) {
      return `[${citation.authorityText}](${kAgentV2LegalCitationHrefPrefix}${encodeURIComponent(citation.citationId)})`
    }

    return `[[${labeled.label ?? kAgentV2UnverifiedCitationLabel}]](${kAgentV2CitationHrefPrefix}${encodeURIComponent(citation.citationId)})`
  })
}
